'use client'

import clsx from 'clsx'

import { type JSX, useEffect, useState } from 'react'

import Image from 'next/image'

import { Chip, Surface } from '../design-system'
import { BUBBLE, MockFoot } from './mock-parts'
import useReducedMotion from './use-reduced-motion'
import useRevealOnView from './use-reveal-on-view'

// `setupCareerGPT` (`app.js:963-1167`), timings unchanged: the thread fades out for 160ms, the answer
// "thinks" for 900ms, then streams a word every 42ms, and the next question starts 7600ms after the
// previous one did. It starts when 32% of it is in view.
const FADE_MS = 160
const THINK_MS = 900
const WORD_MS = 42
const ROTATE_MS = 7600

// Copy from app.js:970-997, unchanged. The first entry has no starter chip.
const QUESTIONS = [
  {
    starter: null,
    q: 'Do stylists have to build their own client book?',
    answer:
      "No. Most locations provide steady walk-in traffic from day one, so you don't need to bring your own client book. Stylists typically serve 12 to 20 guests a day, with paid training and a ",
    highlight: '401(k) match',
    ending: ' at participating salons.',
  },
  {
    starter: 'Health benefits?',
    q: 'What health benefits do you offer?',
    answer: 'Full-time stylists get medical, dental and vision from day one, plus a ',
    highlight: '401(k) match',
    ending: ' and paid training at participating locations. Part-time roles include discounted plans.',
  },
  {
    starter: 'No weekend shifts?',
    q: 'Can I work weekdays only?',
    answer: 'Yes. Many salons hire for weekday-only schedules and most close by 6pm. Roles with ',
    highlight: 'no late closes',
    ending: ' are flagged right in the results, so you can filter for them.',
  },
  {
    starter: 'Career growth?',
    q: 'What does career growth look like?',
    answer: 'Stylists step up to senior stylist, educator and salon manager tracks, with ',
    highlight: 'paid training',
    ending: ' and structured reviews every six months. Several managers started behind the chair.',
  },
] as const

// Each answer as words, per part (lead-in, highlight, ending), split the way production streams them.
const WORDS = QUESTIONS.map(({ answer, highlight, ending }) =>
  [answer, highlight, ending].map((text) => text.match(/\s*\S+\s*/g) ?? []),
)
const TOTALS = WORDS.map((parts) => parts.flat().length)

// The first `count` words of an answer, still split into its three parts.
function streamed(index: number, count: number): string[] {
  let left = count

  return WORDS[index].map((part) => {
    const taken = part.slice(0, Math.max(0, left)).join('')
    left -= part.length

    return taken
  })
}

type Chat = {
  /** The question on screen, and the one asked for (they differ while the thread fades). */
  shown: number
  target: number
  phase: 'thinking' | 'streaming' | 'done'
  words: number
  fading: boolean
  /** Bumped on every question asked: restarts the rotation timer. */
  run: number
  /** Bumped every time a question lands: remounts the bubbles, so their pop-in replays. */
  turn: number
}

// Asking fades the thread out first; `advance` swaps the question in once the fade is over.
function ask(pick: (chat: Chat) => number): (chat: Chat) => Chat {
  return (chat) => ({ ...chat, target: pick(chat), fading: true, run: chat.run + 1 })
}
const askFirst = ask(() => 0)
const askNext = ask((chat) => (chat.target + 1) % QUESTIONS.length)

// One step of the choreography; which step, and after how long, depends only on the state.
function advance(chat: Chat): Chat {
  if (chat.fading)
    return { ...chat, shown: chat.target, phase: 'thinking', words: 0, fading: false, turn: chat.turn + 1 }
  if (chat.phase === 'thinking') return { ...chat, phase: 'streaming' }
  // Production's interval ticks once more after the last word before it shows the sources.
  if (chat.words < TOTALS[chat.shown]) return { ...chat, words: chat.words + 1 }

  return { ...chat, phase: 'done' }
}

function delay(chat: Chat): number {
  if (chat.fading) return FADE_MS
  if (chat.phase === 'thinking') return THINK_MS

  return WORD_MS
}

// Static, so hoisted. The dots take the muted ink of the current theme, as production's
// `var(--sj-color-ink-muted)` does.
const thinking = (
  <span aria-hidden="true" className="inline-flex h-[1.55em] items-center gap-[5px]">
    <i className="block size-[5px] animate-chat-blink rounded-full bg-ink-muted" />
    <i className="block size-[5px] animate-chat-blink rounded-full bg-ink-muted [animation-delay:0.16s]" />
    <i className="block size-[5px] animate-chat-blink rounded-full bg-ink-muted [animation-delay:0.32s]" />
  </span>
)

/**
 * The simulated CareerGPT chat in the home page's browser mock-up — not the real widget. It asks a
 * canned question, "thinks", streams the answer word by word and cites its sources, then moves to the
 * next question; the starter chips and the send button jump the queue.
 *
 * Everything on screen renders from one piece of state. Two timers drive it, one pending at a time
 * each and both cleared on cleanup: the choreography (fade, think, one word) and the rotation.
 *
 * Under reduced motion it shows the whole conversation, schedules nothing, and a chip or the send
 * button swaps the answer in complete. The server renders the same static first answer, as
 * production's first `render(order[0], false)` does.
 */
export default function CareerGptMock(): JSX.Element {
  const reducedMotion = useReducedMotion()
  const { ref, visible } = useRevealOnView<HTMLDivElement>({ threshold: 0.32 })
  const [chat, setChat] = useState<Chat>({
    shown: 0,
    target: 0,
    phase: 'done',
    words: TOTALS[0],
    fading: false,
    run: 0,
    turn: 0,
  })

  const settled = chat.phase === 'done' && !chat.fading
  useEffect(() => {
    if (reducedMotion || settled) return undefined

    const timer = window.setTimeout(() => setChat(advance), delay(chat))

    return (): void => window.clearTimeout(timer)
  }, [reducedMotion, settled, chat])

  // The rotation. Nothing has been asked yet the first time it comes into view, so it asks the first
  // question straight away, replaying it with motion as production does; after that, the next one.
  const started = chat.run > 0
  useEffect(() => {
    if (!visible || reducedMotion) return undefined

    const timer = window.setTimeout(() => setChat(started ? askNext : askFirst), started ? ROTATE_MS : 0)

    return (): void => window.clearTimeout(timer)
  }, [visible, reducedMotion, started, chat.run])

  const index = reducedMotion ? chat.target : chat.shown
  const phase = reducedMotion ? 'done' : chat.phase
  const item = QUESTIONS[index]
  const [head, highlight, tail] =
    phase === 'done' ? [item.answer, item.highlight, item.ending] : streamed(index, chat.words)
  // Nothing pops on the static first render, nor under reduced motion.
  const animated = !reducedMotion && started

  return (
    <Surface ref={ref}>
      <div className="flex min-h-[44px] items-center gap-[14px] border-b border-border bg-surface-chrome px-[14px] py-[9px]">
        <span aria-hidden="true" className="flex gap-[5px]">
          <i className="block size-[6px] bg-fill-strong" />
          <i className="block size-[6px] bg-fill-strong" />
          <i className="block size-[6px] bg-fill-strong" />
        </span>
        <span className="text-3xs text-ink-secondary">yourbrand.com/careers</span>
      </div>

      <div className="min-h-[500px] p-[22px] max-[761px]:min-h-[480px] max-[761px]:p-[16px]">
        <div className="flex items-center gap-[9px] text-base font-semibold text-ink">
          <Image
            src="/assets/inline/b830eccfbc03.png"
            alt=""
            width={220}
            height={220}
            sizes="24px"
            className="size-[24px] object-contain"
          />
          Ask anything about working here
        </div>

        <div className="mt-[22px] grid min-h-[48px] grid-cols-[24px_minmax(0,1fr)_44px] items-center gap-[10px] rounded-xl border border-border bg-fill-subtle py-[5px] pr-[5px] pl-[12px] shadow-float">
          <Image
            src="/assets/inline/63f97fa79514.png"
            alt=""
            width={220}
            height={220}
            sizes="24px"
            className="size-[24px] object-contain"
          />
          <span className="truncate text-xs text-ink-secondary">Ask about pay, schedules, growth…</span>
          <button
            type="button"
            aria-label="Send question"
            onClick={(): void => setChat(askNext)}
            className="grid size-[44px] cursor-pointer place-items-center rounded-full border-0 bg-accent p-0 font-sans font-semibold text-[16px] text-canvas focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            ↑
          </button>
        </div>

        <div className="mt-[12px] flex flex-wrap gap-[7px]">
          {QUESTIONS.map(({ starter }, starterIndex) =>
            starter ? (
              <Chip
                key={starter}
                interactive
                variant={starterIndex === index ? 'accent' : 'default'}
                aria-pressed={starterIndex === index}
                onClick={(): void => setChat(ask(() => starterIndex))}
              >
                {starter}
              </Chip>
            ) : null,
          )}
        </div>

        <div
          className={clsx(
            'mt-[24px] flex min-h-[270px] flex-col justify-end gap-[14px] border-t border-border pt-[24px] transition-[opacity,translate] duration-[160ms] ease-[ease] max-[761px]:min-h-[250px]',
            chat.fading && !reducedMotion && 'translate-y-[8px] opacity-0',
          )}
        >
          <div key={`q${chat.turn}`} className={clsx(BUBBLE, animated && 'animate-chat-pop')}>
            {item.q}
          </div>
          <div
            key={`a${chat.turn}`}
            className={clsx(
              'max-w-[92%] rounded-lg bg-fill-subtle p-[14px] text-2xs text-ink',
              phase === 'thinking' ? 'min-h-0' : 'min-h-[102px]',
            )}
          >
            {phase === 'thinking' && thinking}
            <span
              className={clsx(
                phase === 'streaming' &&
                  "after:ml-[2px] after:inline-block after:h-[0.95em] after:w-[2px] after:animate-chat-caret after:bg-accent after:align-[-0.12em] after:content-['']",
              )}
            >
              {head}
              <span className="font-semibold text-accent">{highlight}</span>
              {tail}
            </span>
            {phase === 'done' && (
              <div className={clsx('mt-[14px] flex flex-wrap gap-[7px]', animated && 'animate-chat-pop-sources')}>
                <Chip variant="accent">careers site</Chip>
                <Chip>glassdoor.com</Chip>
                <Chip>indeed.com</Chip>
              </div>
            )}
          </div>
        </div>
      </div>

      <MockFoot>
        <span>Answer coverage 87%</span>
        <span>·</span>
        <span>2,412 prompts captured this month</span>
      </MockFoot>
    </Surface>
  )
}
